import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

function Settings({ onUpdate }) {
  const [settings, setSettings] = useState({
    google_client_id: '',
    google_client_secret: '',
    google_redirect_uri: 'https://budget.jmhm.com',
    app_name: 'Budget Tracker',
  });
  const [favicon, setFavicon] = useState(null);
  const [currentFavicon, setCurrentFavicon] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      const data = await api.getSettings();
      setSettings(prev => ({ ...prev, ...data }));
      
      // Load current favicon if set
      if (data.favicon) {
        setCurrentFavicon(data.favicon);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      await api.updateSettings(settings);
      
      if (favicon) {
        await api.uploadFavicon(favicon);
        onUpdate();
      }
      
      alert('Settings saved successfully!');
    } catch (error) {
      alert('Failed to save settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings-page">
      <h2>Settings</h2>

      <section className="settings-section">
        <h3>General Settings</h3>

        <div className="form-group">
          <label>Application Name</label>
          <input
            type="text"
            value={settings.app_name || ''}
            onChange={(e) => setSettings({ ...settings, app_name: e.target.value })}
            className="input-text"
            placeholder="Budget Tracker"
          />
          <small className="form-hint">
            This name appears in the header and browser tab
          </small>
        </div>
      </section>

      <section className="settings-section">
        <h3>Google OAuth Configuration</h3>
        <p className="settings-hint">
          Configure Google OAuth to enable authentication. Leave blank to disable authentication.
        </p>

        <div className="form-group">
          <label>Client ID</label>
          <input
            type="text"
            value={settings.google_client_id || ''}
            onChange={(e) => setSettings({ ...settings, google_client_id: e.target.value })}
            className="input-text"
            placeholder="Your Google OAuth Client ID"
          />
        </div>

        <div className="form-group">
          <label>Client Secret</label>
          <input
            type="password"
            value={settings.google_client_secret || ''}
            onChange={(e) => setSettings({ ...settings, google_client_secret: e.target.value })}
            className="input-text"
            placeholder="Your Google OAuth Client Secret"
          />
        </div>

        <div className="form-group">
          <label>Redirect URI</label>
          <input
            type="text"
            value={settings.google_redirect_uri || ''}
            onChange={(e) => setSettings({ ...settings, google_redirect_uri: e.target.value })}
            className="input-text"
            placeholder="https://budget.jmhm.com"
          />
          <small className="form-hint">
            This must match the authorized redirect URI in your Google Cloud Console (just the base URL, no /auth/callback)
          </small>
        </div>
      </section>

      <section className="settings-section">
        <h3>Appearance</h3>

        <div className="form-group">
          <label>Favicon</label>
          
          {currentFavicon && (
            <div className="favicon-preview">
              <img src={currentFavicon} alt="Current favicon" />
              <span>Current favicon</span>
            </div>
          )}
          
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFavicon(e.target.files[0])}
            className="input-file"
          />
          <small className="form-hint">Upload a custom favicon for your budget tracker (16x16 or 32x32 pixels recommended)</small>
        </div>
      </section>

      <div className="settings-actions">
        <button onClick={handleSave} disabled={saving} className="btn btn-primary">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <section className="settings-section">
        <h3>Setup Instructions</h3>
        <div className="instructions">
          <h4>Google OAuth Setup:</h4>
          <ol>
            <li>Go to the <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
            <li>Create a new project or select an existing one</li>
            <li>Enable the Google+ API</li>
            <li>Go to "Credentials" and create an OAuth 2.0 Client ID</li>
            <li>Add your redirect URI: <strong>https://budget.jmhm.com</strong> (just the base URL, no path)</li>
            <li>Copy the Client ID and Client Secret to the fields above</li>
            <li>Save the settings and refresh the page</li>
          </ol>
        </div>
      </section>
    </div>
  );
}

export default Settings;
